import { Globe, Server, ShoppingCart, UsersRound, MessageSquareText, Workflow, CheckCircle2 } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const OFFERINGS = [
  {
    icon: Globe,
    title: "Corporate Websites & Landing Pages",
    desc: "Fast, credible, conversion-first sites — the kind that make a business look like what it's about to become.",
  },
  {
    icon: Server,
    title: "Domain & Web Hosting",
    desc: "Domains, hosting, email and SSL — procured, configured and renewed without you thinking about any of it.",
  },
  {
    icon: ShoppingCart,
    title: "E-Commerce Solutions",
    desc: "Catalogue, cart, payments and delivery — stores built to take orders, not just show products.",
  },
  {
    icon: UsersRound,
    title: "CRM Setup & Lead Automation",
    desc: "Every enquiry captured, assigned and followed up automatically — no lead dies in a WhatsApp archive again.",
  },
  {
    icon: MessageSquareText,
    title: "WhatsApp API Integrations",
    desc: "Official WhatsApp Business API wired into your funnel — broadcasts, auto-replies and lead flows that never sleep.",
  },
];

const IT_SERVICES = [
  "Corporate Website / Landing Page", "Domain & Web Hosting", "E-Commerce Solutions",
  "CRM Setup & Lead Automation", "WhatsApp API Integration",
];

const STACK_GROUPS = [
  { label: "Web & Frontend", items: ["React", "Next.js", "TypeScript", "Tailwind CSS", "WordPress", "Elementor"] },
  { label: "Backend & Databases", items: ["Node.js", "Python", "PHP", "MongoDB", "MySQL", "Firebase"] },
  { label: "E-Commerce & Payments", items: ["WooCommerce", "Shopify", "Razorpay", "PayU", "Paytm Business"] },
  { label: "Hosting & Infrastructure", items: ["AWS", "Google Cloud", "Cloudflare", "cPanel Hosting", "SSL & Domains", "Business Email"] },
  { label: "CRM & Automation", items: ["Zoho CRM", "HubSpot", "Lead Automation", "Zapier", "n8n", "WhatsApp Business API"] },
  { label: "Marketing & Analytics", items: ["Meta Ads Manager", "Google Ads", "Google Analytics (GA4)", "Google Tag Manager", "Search Console", "SEMrush"] },
];

export default function ITServices() {
  return (
    <div data-testid="it-page">
      <PageHero
        eyebrow="Digital Infrastructure"
        title={["IT", "Services."]}
        sub="Your entire digital stack — website, hosting, store, CRM and WhatsApp — designed, deployed and maintained by one team, so infrastructure never becomes your bottleneck."
      >
        <ScrollCta href="#project" label="Start a Project" testId="it-start-project-btn" />
      </PageHero>

      {/* OFFERINGS */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="it-offerings-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Capabilities</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              From domain to lead automation.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {OFFERINGS.map((o, i) => (
              <Reveal key={o.title} delay={i * 0.06}>
                <div className="card-lift h-full rounded-3xl border border-slate-200 bg-white p-7" data-testid={`it-offering-${i + 1}`}>
                  <o.icon className="h-8 w-8 text-amber-600" />
                  <h3 className="mt-6 font-display text-xl font-extrabold text-navy">{o.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{o.desc}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="grain flex h-full flex-col justify-between rounded-3xl bg-navy p-7">
                <Workflow className="h-8 w-8 text-gold" />
                <div>
                  <h3 className="mt-8 font-display text-xl font-extrabold text-white">Streamlined by default.</h3>
                  <p className="mt-2 text-sm text-white/65">
                    One vendor, one invoice, one accountable team — infrastructure that just works, quietly.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STACK */}
      <section className="grain relative bg-navy py-16 sm:py-20" data-testid="it-stack-section">
        <div className="container-x relative z-10">
          <Reveal>
            <p className="eyebrow !text-gold">Technologies we build on</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Modern stack. Zero drama.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {STACK_GROUPS.map((group, gi) => (
              <Reveal key={group.label} delay={gi * 0.07}>
                <div className="h-full rounded-3xl border border-white/10 bg-white/5 p-6 transition-colors duration-300 hover:border-gold/50" data-testid={`it-stack-group-${gi + 1}`}>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">{group.label}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {group.items.map((tech) => (
                      <span
                        key={tech}
                        className="inline-block rounded-full border border-white/20 bg-white/5 px-3.5 py-1.5 text-xs font-semibold text-white/85 transition hover:border-gold hover:text-gold"
                        data-testid={`it-stack-chip-${tech.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECT FORM */}
      <section id="project" className="py-16 sm:py-24" data-testid="it-project-section">
        <div className="container-x grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">New Project</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Start a project.
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              Tell us what needs building — the tech desk responds with a scope, a timeline and a number. No jargon, no
              vague estimates.
            </p>
            <ul className="mt-8 space-y-3">
              {["Fixed-scope proposals", "Delivery timelines in writing", "Post-launch support included"].map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-sm font-bold text-navy">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                  {pt}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8" data-testid="it-project-form-card">
              <LeadForm
                source="it-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={IT_SERVICES}
                cta="Start a Project"
                idPrefix="it-project"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
